import React, { useState, useEffect, useReducer, useRef } from "react";
import { isSameDay, parseISO, format } from "date-fns";
import clsx from "clsx";
import { green } from "@material-ui/core/colors";
import {
  Button,
  CircularProgress,
  Divider,
  IconButton,
  makeStyles,
  Fab,
  Badge,
  Drawer,
  InputBase,
  Typography
} from "@material-ui/core";
import {
  AccessTime,
  Block,
  Edit,
  Done,
  DoneAll,
  ExpandMore,
  GetApp,
  Close as CloseIcon,
  ArrowUpward as ArrowUpwardIcon,
  ExpandMore as ExpandMoreIcon,
  PlayCircleOutline as PlayCircleOutlineIcon
} from "@material-ui/icons";

import Player from "../MessagesList/reactPlayer";
import MarkdownWrapper from "../MarkdownWrapper";
import VcardPreview from "../VcardPreview";
import LocationPreview from "../LocationPreview";
import ModalImageCors from "../ModalImageCors";
import ModalImageSticker from "../ModalImageSticker";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import Twemoji from 'react-twemoji';

const useStyles = makeStyles((theme) => ({
  messagesListWrapper: {
    overflow: "hidden",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
  },
  messagesList: {
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    padding: "20px",
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  circleLoading: {
    color: green[500],
    position: "absolute",
    opacity: "70%",
    top: 0,
    left: "50%",
    marginTop: 12,
  },
  messageLeft: {
    marginRight: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },
    textAlign: "justify",
    wordBreak: "break-word",
    whiteSpace: "pre-wrap",
    backgroundColor: theme.messageLeft.backgroundColor,
    color: theme.messageLeft.color,
    alignSelf: "flex-start",
    borderTopLeftRadius: 0,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    padding: "5px 5px 10px 5px",
    boxShadow: "0 1px 1px #b3b3b3",
  },
  quotedContainerLeft: {
    margin: "-3px -80px 6px -6px",
    overflow: "hidden",
    backgroundColor: theme.quotedContainerLeft.backgroundColor,
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },
  quotedMsg: {
    padding: 10,
    maxWidth: 400,
    height: "auto",
    display: "block",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
    wordBreak: "break-word",
  },
  quotedSideColorLeft: { flex: "none", width: "4px", backgroundColor: "#6bcbef" },
  messageRight: {
    marginLeft: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },
    textAlign: "justify",
    wordBreak: "break-word",
    whiteSpace: "pre-wrap",
    backgroundColor: "#dcf8c6",
    color: "#303030",
    alignSelf: "flex-end",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 0,
    padding: "5px 5px 10px 5px",
    boxShadow: "0 1px 1px #b3b3b3",
  },
  quotedContainerRight: {
    margin: "-3px -80px 6px -6px",
    overflowY: "hidden",
    backgroundColor: "#cfe9ba",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },
  quotedSideColorRight: { flex: "none", width: "4px", backgroundColor: "#35cd96" },
  messageActionsButton: {
    display: "none",
    position: "relative",
    color: "#999",
    zIndex: 1,
    backgroundColor: "inherit",
    opacity: "90%",
    "&:hover, &.Mui-focusVisible": { backgroundColor: "inherit" },
  },
  messageContactName: { display: "flex", color: theme.messageContactName.color, fontWeight: 500 },
  textContentItem: { overflowWrap: "break-word", padding: "3px 80px 6px 6px" },
  textContentItemDeleted: { fontStyle: "italic", color: "rgba(0, 0, 0, 0.36)", overflowWrap: "break-word", padding: "3px 80px 6px 6px" },
  messageMedia: { objectFit: "cover", width: 250, height: 200, borderRadius: 8 },
  timestamp: { fontSize: 11, position: "absolute", bottom: 0, right: 5, color: "#999" },
  dailyTimestamp: { alignItems: "center", textAlign: "center", alignSelf: "center", width: "110px", backgroundColor: "#e1f3fb", margin: "10px", borderRadius: "10px", boxShadow: "0 1px 1px #b3b3b3" },
  dailyTimestampText: { color: "#808888", padding: 8, alignSelf: "center" },
  ackIcons: { fontSize: 18, verticalAlign: "middle", marginLeft: 4 },
  deletedIcon: { fontSize: 18, verticalAlign: "middle", marginRight: 4 },
  ackDoneAllIcon: { color: green[500], fontSize: 18, verticalAlign: "middle", marginLeft: 4 },
  downloadMedia: { display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "inherit", padding: 10 },
  scrollToBottom: { position: 'fixed', margin: '0px', top: 'auto', right: '10px', bottom: '150px', left: 'auto' },
  header: { display: "flex", borderBottom: "1px solid rgba(0, 0, 0, 0.12)", backgroundColor: theme.palette.background.default, alignItems: "center", padding: theme.spacing(0, 1), minHeight: "73px" },
  drawer: { "& .MuiDrawer-paper": { width: 400, maxHeight: 'calc(100% - 80px)', position: "absolute" } },
  searchInput: { flex: 1, border: "none", borderRadius: 25, padding: "10px", outline: "none" },
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_MESSAGES") {
    const messages = action.payload;
    const newMessages = [];
    messages.forEach((message) => {
      const messageIndex = state.findIndex((m) => m.id === message.id);
      if (messageIndex !== -1) {
        state[messageIndex] = message;
      } else {
        newMessages.push(message);
      }
    });
    return [...newMessages, ...state].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
  }
  if (action.type === "RESET") return [];
  return state;
};

const MessagesListDrawer = ({ openDrawer, closeDrawerHandle, contactId, isGroup, ticketId }) => {
  const classes = useStyles();
  const [messagesList, dispatch] = useReducer(reducer, []);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const lastMessageRef = useRef();
  const [modalOpen, setModalOpen] = useState({ urlVideo: '', open: false });
  const [messagePendent, setMessagePendent] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [lastSearch, setLastSearch] = useState("");
  const [searchPage, setSearchPage] = useState(1);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setSearchInput("");
    setLastSearch("");
    setSearchPage(1);
  }, [contactId]);

  const handleInitialSearch = async () => {
    if (!searchInput) return;
    setLoading(true);

    let pageToFetch = 1;
    if (searchInput === lastSearch) {
      pageToFetch = !hasMore ? 1 : searchPage + 1;
    } else {
      setLastSearch(searchInput);
      setSearchPage(1);
    }

    dispatch({ type: "RESET" });

    try {
      const { data: searchData } = await api.get("/messages/search/" + ticketId, {
        params: { 
          searchParam: JSON.stringify({ input: searchInput, flag: false }),
          pageNumber: pageToFetch 
        },
      });

      if (searchData.messages && searchData.messages.length > 0) {
        dispatch({ type: "LOAD_MESSAGES", payload: searchData.messages });
        setHasMore(searchData.hasMore);
        setSearchPage(pageToFetch);

        setTimeout(() => {
          const container = document.getElementById("messagesList1");
          if (container) {
            container.scrollTop = container.scrollHeight / 2 - container.clientHeight / 2;
          }
        }, 800);
      } else {
        toastError({ response: { data: { message: "Nenhuma mensagem encontrada." } } });
      }
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const downScroll = () => {
    if (lastMessageRef.current) {
      lastMessageRef.current.scrollIntoView({});
    }
    setMessagePendent(0);
  };

  const checkMessageMedia = (message) => {
    if (message.mediaType === "location" && message.body.split('|').length >= 2) {
      let locationParts = message.body.split('|');
      return <LocationPreview image={locationParts[0]} link={locationParts[1]} description={locationParts[2] || null} />
    }
    else if (message.mediaType === "vcard") {
      let array = message.body.split("\n");
      let obj = [];
      let contact = "";
      for (let index = 0; index < array.length; index++) {
        const v = array[index];
        let values = v.split(":");
        for (let ind = 0; ind < values.length; ind++) {
          if (values[ind].indexOf("+") !== -1) obj.push({ number: values[ind] });
          if (values[ind].indexOf("FN") !== -1) contact = values[ind + 1];
        }
      }
      if (typeof obj[0] === 'undefined') return null;
      return <VcardPreview contact={contact} numbers={obj[0].number} />
    }
    else if (message.mediaType === "sticker") return <ModalImageSticker imageUrl={message.mediaUrl} />;
    else if (message.mediaType === "image") return <ModalImageCors imageUrl={message.mediaUrl} />;
    else if (message.mediaType === "audio") return (
      <audio controls><source src={message.mediaUrl} type="audio/ogg"></source></audio>
    );
    else if (message.mediaType === "video") return (
      <div>
        <video className={classes.messageMedia} src={message.mediaUrl} controls />
        <Fab size="small" onClick={() => setModalOpen({ urlVideo: message.mediaUrl, open: true })}>
          <PlayCircleOutlineIcon color="primary" />
        </Fab>
      </div>
    );
    else {
      return (
        <>
          <div className={classes.downloadMedia}>
            <Button startIcon={<GetApp />} color="primary" variant="outlined" target="_blank" href={message.mediaUrl}>Download</Button>
          </div>
          <Divider />
        </>
      );
    }
  };

  const renderMessageAck = (message) => {
    if (message.ack === 0) return <AccessTime fontSize="small" className={classes.ackIcons} />;
    if (message.ack === 1) return <Done fontSize="small" className={classes.ackIcons} />;
    if (message.ack === 2) return <DoneAll fontSize="small" className={classes.ackIcons} />;
    if (message.ack === 3 || message.ack === 4) return <DoneAll fontSize="small" className={classes.ackDoneAllIcon} />;
  };

  const getStringColor = (string = '', saturation = 100, lightness = 75) => {
    let hash = 0;
    for (let i = 0; i < string.length; i++) {
      hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }
    return `hsl(${(hash % 360)}, ${saturation}%, ${lightness}%)`;
  };

  const renderDailyTimestamps = (message, index) => {
    if (index === 0 || !isSameDay(parseISO(messagesList[index].createdAt), parseISO(messagesList[index - 1].createdAt))) {
      return (
        <span className={classes.dailyTimestamp} key={`timestamp-${message.id}`}>
          <div className={classes.dailyTimestampText}>{format(parseISO(message.createdAt), "dd/MM/yyyy")}</div>
        </span>
      );
    }
  };

  const renderQuotedMessage = (message) => (
    <div className={clsx(classes.quotedContainerLeft, { [classes.quotedContainerRight]: message.fromMe })}>
      <span className={clsx(classes.quotedSideColorLeft, { [classes.quotedSideColorRight]: message.quotedMsg?.fromMe })}></span>
      <div className={classes.quotedMsg}>
        {!message.quotedMsg?.fromMe && <span className={classes.messageContactName}>{message.quotedMsg?.contact?.name}</span>}
        {(message.quotedMsg.mediaUrl || ["location", "vcard"].includes(message.quotedMsg.mediaType)) && checkMessageMedia(message.quotedMsg)}
        {message.quotedMsg?.body}
      </div>
    </div>
  );

const renderMessages = () => {
  return messagesList.map((message, index) => {
    // 1. Lógica para identificar se a mensagem deve ser destacada
    // Convertemos ambos para minúsculo para a busca não ser sensível a maiúsculas/minúsculas
    const isHighlighted = searchInput && 
      message.body?.toLowerCase().includes(searchInput.toLowerCase());

    return (
      <React.Fragment key={message.id}>
        {renderDailyTimestamps(message, index)}
        <div 
          className={clsx(
            message.fromMe ? classes.messageRight : classes.messageLeft,
            { [classes.highlightedMessage]: isHighlighted } // Adiciona uma classe CSS se houver match
          )}
          // 2. Estilo inline para garantir a troca de cor (ajuste as cores como desejar)
          style={isHighlighted ? { 
            // Se for minha (fromMe), verde claro. Se for dos outros, cinza 616161.
            backgroundColor: message.fromMe ? '#ebf9e1' : '#616161', 
            border: '2px solid #00bfa5', 
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)', 
            transition: 'all 0.3s ease',
            // Ajusta a cor do texto: preto para o verde claro (melhor leitura) e branco para o cinza
            color: message.fromMe ? '#000000' : '#ffffff',
            borderRadius: '8px'
          } : {}}
          >
          <IconButton 
            variant="contained" 
            size="small" 
            id="messageActionsButton" 
            disabled={message.isDeleted} 
            className={classes.messageActionsButton}
          >
            <ExpandMore />
          </IconButton>

          {isGroup && !message.fromMe && (
            <span className={classes.messageContactName} style={{ color: getStringColor(message.contact?.number) }}>
              {message.contact?.name}
            </span>
          )}

          {(message.mediaUrl || ["location", "vcard", "sticker", "image", "audio", "video"].includes(message.mediaType)) && checkMessageMedia(message)}

          {message.mediaType === "call_log" && (
            <div>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 17" width="20" height="17">
                <path fill="#df3333" d="M18.2 12.1c-1.5-1.8-5-2.7-8.2-2.7s-6.7 1-8.2 2.7c-.7.8-.3 2.3.2 2.8.2.2.3.3.5.3 1.4 0 3.6-.7 3.6-.7.5-.2.8-.5.8-1v-1.3c.7-1.2 5.4-1.2 6.4-.1l.1.1v1.3c0 .2.1.4.2.6.1.2.3.3.5.4 0 0 2.2.7 3.6.7.2 0 1.4-2 .5-3.1zM5.4 3.2l4.7 4.6 5.8-5.7-.9-.8L10.1 6 6.4 2.3h2.5V1H4.1v4.8h1.3V3.2z"></path>
              </svg> <span>Chamada de voz/vídeo</span>
            </div>
          )}

          <div className={clsx(classes.textContentItem, { [classes.textContentItemDeleted]: message.isDeleted })}>
            {/* Bloco de Mensagem Interna adicionado aqui */}
            {message.isInternal && (
              <Typography 
                variant="inherit" 
                style={{ 
                  color: '#0000FF', // Azul puro (ou use 'blue')
                  fontWeight: 'bold',
                  display: 'block',
                  marginBottom: '4px' 
                }}
              > 
                Mensagem Interna
              </Typography>
            )}
            
            {message.isDeleted && <Block color="disabled" fontSize="small" className={classes.deletedIcon} />}
            {message.isEdit && <Edit color="disabled" fontSize="small" className={classes.deletedIcon} />}
            {message.quotedMsg && renderQuotedMessage(message)}
            
            <Twemoji options={{ className: 'twemoji' }}>
              <MarkdownWrapper>{message.body}</MarkdownWrapper>
            </Twemoji>

            <span className={classes.timestamp}>
              {format(parseISO(message.createdAt), "HH:mm")}
              {message.fromMe && renderMessageAck(message)}
            </span>
          </div>
        </div>
        {index === messagesList.length - 1 && <div ref={lastMessageRef} style={{ float: "left", clear: "both" }} />}
      </React.Fragment>
    );
  });
};

  return (
    <Drawer className={classes.drawer} variant="persistent" anchor="right" open={openDrawer}>
      <div className={classes.header}>
        <IconButton onClick={closeDrawerHandle}><CloseIcon style={{ color: "#30a3e6" }} /></IconButton>
        <InputBase
          className={classes.searchInput}
          placeholder="Buscar (20 antes e 20 depois)..."
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleInitialSearch()}
        />
        <IconButton onClick={handleInitialSearch}><ArrowUpwardIcon style={{ color: "#30a3e6" }} /></IconButton>
      </div>
      <div className={classes.messagesListWrapper}>
        <div id="messagesList1" className={classes.messagesList}>
          {messagesList.length > 0 ? renderMessages() : []}
          {messagePendent > 0 && (
            <Badge badgeContent={messagePendent} color="secondary" className={classes.scrollToBottom}>
              <Fab size="small" onClick={() => downScroll()}><ExpandMoreIcon /></Fab>
            </Badge>
          )}
        </div>
        {loading && <CircularProgress className={classes.circleLoading} />}
        {modalOpen.open && <Player modalOpen={modalOpen} />}
      </div>
    </Drawer>
  );
};

export default MessagesListDrawer;